import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getLocations } from '../utils/api';
import { storeRwgToken } from '../utils/rwgToken';
import { parseCalendarRouteId } from '../utils/deepLink';
import { useBranding } from './BrandingContext';
import {
  LOAD_FAILURE_REASONS,
  applyCalendarAnalyticsContext,
  initWidgetAnalytics,
  startOpenTracking,
  stopOpenTracking,
  trackWidgetClosed,
  trackWidgetLoadFailed,
} from '../analytics';

const CalendarContext = createContext();

export const useCalendar = () => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error('useCalendar must be used within a CalendarProvider');
  }
  return context;
};

export const CalendarProvider = ({ children }) => {
  // CIN-6879: Reserve with Google publishes the composite entity id
  // `<calendar_id>~<calendar_location_id>` as its Merchant ID, and Google's own
  // verification URL puts it straight in the path. Split it here so every
  // downstream consumer (the locations fetch, the session cookie name, the
  // rwg_token storage key) sees a real calendar uuid; the location half is fed
  // in as a fallback for locationIdParam, i.e. it behaves exactly like
  // `?location=`. A plain single-uuid route param is unaffected.
  const { calendarId: rawRouteCalendarId } = useParams();
  const { calendarId: routeCalendarId, locationId: routeLocationId } =
    parseCalendarRouteId(rawRouteCalendarId);
  const location = useLocation();
  const navigate = useNavigate();
  const { setBranding, setVariant, setWidgetConfig } = useBranding();
  
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calendarId, setCalendarId] = useState(routeCalendarId || null);
  const [validatePhone, setValidatePhone] = useState(false);
  const [locationIdParam, setLocationIdParam] = useState(false);
  const [fields, setFields] = useState({});
  // CIN-7181: calendar-level Wait / Drop-off flag. Strictly true only, so an
  // older backend (key absent) keeps today's flow.
  const [enableWaiterDropoff, setEnableWaiterDropoff] = useState(false);
  // The open tracker holds a window message listener; drop it with the provider.
  useEffect(() => stopOpenTracking, []);

  useEffect(() => {
    // Check for query parameter and redirect if needed
    const urlParams = new URLSearchParams(location.search);
    const queryCalendarId = urlParams.get('calendarId');

    // CIN-6442: capture the Reserve with Google conversion token on landing.
    // URLSearchParams.get returns the DECODED value (preserves '='). A URL
    // token replaces any stored token and resets the 30-day window. Runs before
    // the redirect early-return so it is captured in both routing paths.
    const rwgToken = urlParams.get('rwg_token');
    if (rwgToken) {
      storeRwgToken(rwgToken, routeCalendarId || queryCalendarId);
    }

    if (!routeCalendarId && queryCalendarId) {
      // Redirect to route-based URL
      urlParams.delete('calendarId');
      const newSearch = urlParams.toString();
      navigate(`/${queryCalendarId}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
      return;
    }

    // Get variant from query parameters
    const variantParam = urlParams.get('variant') || 'v1';
    
    // Check if locationId parameter exists
    // CIN-6879: an explicit query param WINS; the `~` path half is the fallback,
    // so `?location=` behavior is byte-for-byte unchanged.
    const pinnedLocationId = urlParams.get('locationId') || urlParams.get('location') || routeLocationId;
    setLocationIdParam(pinnedLocationId);

    const finalCalendarId = routeCalendarId || queryCalendarId;

    // Before the fetch, so a widget that never loads still reports where it
    // failed and on whose page (CIN-7080).
    const { arrivalPath } = initWidgetAnalytics({
      calendarId: finalCalendarId,
      variant: variantParam,
      searchParams: urlParams,
      hasRwgToken: !!rwgToken,
    });

    if (!finalCalendarId) {
      trackWidgetLoadFailed({ failureReason: LOAD_FAILURE_REASONS.MISSING_CALENDAR });
      setError('Calendar not properly initialized. Please provide a calendar ID.');
      setLoading(false);
      return;
    }

    setCalendarId(finalCalendarId);
    setVariant(variantParam);
    console.log(`Widget variant: ${variantParam}`);

    // Check for test configuration (demo page only)
    // Wrap in try-catch to handle cross-origin access restrictions
    try {
      if (window.parent && window.parent.widgetTestConfig) {
        setWidgetConfig(window.parent.widgetTestConfig);
        console.log('Using demo test config:', window.parent.widgetTestConfig);
      }
    } catch (e) {
      // Cross-origin access blocked - this is expected when running in production
      // The widget will use default config from BrandingContext
      console.log('Widget running in cross-origin context, using default config');
    }

    // Fetch locations data
    const fetchData = async () => {
      try {
        const data = await getLocations(finalCalendarId);

        // Extract branding info from settings in locations response
        if (data && data.settings) {
          const settings = data.settings;
          console.log('Found branding settings:', settings);
          setBranding({
            // CIN-6784: sanitized company name from the public settings payload.
            // Reserve with Google requires the merchant name on the landing page to
            // match the entity feed; the backend returns the same sanitized value.
            // Absent (older backend) -> null, and the header renders as before.
            company_name: settings.company_name || null,
            logo_url: settings.logo_url || null,
            primary_color: settings.primary_color || '#007bff',
            secondary_color: settings.secondary_color || '#6c757d',
            font_family: settings.font_family || 'Arial, sans-serif',
            font_url: settings.font_url || null
          });
          // Set phone validation setting
          setValidatePhone(settings.validate_phone || false);
          console.log('Phone validation setting:', settings.validate_phone || false);
          setEnableWaiterDropoff(settings.enable_waiter_dropoff === true);
        } else {
          console.log('No settings found in locations response, using defaults');
          setBranding({
            company_name: null,
            logo_url: null,
            primary_color: '#007bff',
            secondary_color: '#6c757d',
            font_family: 'Arial, sans-serif',
            font_url: null
          });
          setValidatePhone(false);
          setEnableWaiterDropoff(false);
        }

        // Set locations array from the response
        setLocations(data.locations || []);

        // Data fields configured for this calendar's booking form
        setFields(data.fields || {});

        // Settled here rather than on mount: whether the calendar is Cinch's
        // own is the server's call, and phone verification plus the location
        // skip decide every step's index, so both must be registered before
        // the first step event (CIN-7080).
        const loadedLocations = data.locations || [];
        applyCalendarAnalyticsContext({
          settings: data?.settings,
          arrivalPath,
          locationStepSkipped: loadedLocations.length === 1 || !!pinnedLocationId,
        });
        startOpenTracking({ arrivalPath, locationCount: loadedLocations.length });

        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch locations:', err);
        trackWidgetLoadFailed({
          failureReason: LOAD_FAILURE_REASONS.LOCATIONS_FAILED,
          httpStatus: err?.response?.status,
        });
        setError('Failed to load locations. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, [routeCalendarId, routeLocationId, location, navigate, setBranding, setVariant, setWidgetConfig]);

  // closeWidget() is the RAW close: postMessage CLOSE_WIDGET. It must stay the
  // low-level close so the lead-salvage flow can call it after showing/dismissing
  // the modal (CIN-6274).
  // Every real close funnels through here — the X button and the overlay click
  // via the lead-salvage gate, the salvage modal's own dismiss and success, and
  // the background click — so this is the one place booking_widget_closed can
  // fire exactly once per close (CIN-7080).
  const closeWidget = () => {
    trackWidgetClosed();
    window.parent.postMessage({ type: 'CLOSE_WIDGET' }, '*');
  };

  // CIN-6274 wiring: CalendarProvider is a PARENT of SessionProvider, so it
  // cannot read session state to decide whether to show the salvage modal.
  // LeadSalvageProvider (mounted inside SessionProvider) registers its
  // attemptClose() here; handleBackgroundClick routes through it when present and
  // falls back to the raw close otherwise.
  const closeInterceptorRef = useRef(null);
  const registerCloseInterceptor = useCallback((fn) => {
    closeInterceptorRef.current = fn;
  }, []);

  const handleBackgroundClick = (e) => {
    // Only close if clicking the App background
    if (e.target.className === 'App') {
      if (closeInterceptorRef.current) {
        closeInterceptorRef.current();
      } else {
        closeWidget();
      }
    }
  };

  const value = {
    calendarId,
    locations,
    loading,
    error,
    closeWidget,
    registerCloseInterceptor,
    handleBackgroundClick,
    validatePhone,
    locationIdParam,
    fields,
    enableWaiterDropoff
  };

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
};